import {FC, useEffect, useState} from 'react';
import {ReactComponent as ImportIcon} from '@icons/import.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import {BODY1, defaultPage, defaultPageSize, SUBTITLE1} from '@/utils/constants';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Pagination from '@/shared/components/Pagination';
import Input from '@/shared/components/Inputs/Input';
import Button from '@/shared/components/Button';
import {ReportsModalTablesStyle} from '../UnpaidItemsModal/ReportsModalTables.style';
import {ReactComponent as ArrowUpDownIcon} from '@icons/arrow-up-down.svg';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {OrganizationReportsService} from '@/utils/services/organization-reports.service';
import {downloadFile, getErrorMessage} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {REPORTS_GET_FILE} from '@/utils/enums/organization-reports.enum';
import {getParticipantsSelector, getReportLoadingSelector, getReportParticipants} from '@/store/organization-report';
import {IParticipantsData} from '@/utils/types/organization-reports.interface';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Loader from '@/shared/components/Loader';
import Modal from '@/shared/components/Modal';
import AttendeeDetails from '@/components/Organization/Attendees/AttendeeDetails';
import {attendeesTableStyle} from '@/components/Organization/Attendees/AttendeesTable/AttendeesTable.styles';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';

const columnOptions: {label: string; key: keyof IParticipantsData}[] = [
	{label: 'Bid #', key: 'bidderId'},
	{label: 'First name', key: 'firstName'},
	{label: 'Last name', key: 'lastName'},
	{label: 'Email', key: 'email'},
	{label: 'Phone number', key: 'phoneNumber'},
	{label: 'Highest bid', key: 'highestBid'},
];

const AuctionParticipantsModal: FC = () => {
	const dispatch = useAppDispatch();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const participantList = useAppSelector(getParticipantsSelector);
	const attendee = useAppSelector(getAttendeeSelector);
	const isLoading = useAppSelector(getReportLoadingSelector);

	const [searchName, setSearchName] = useState('');
	const [page, setPage] = useState<number>(defaultPage);
	const [filterValues, setFilterValues] = useState(participantList?.data || []);
	const [sortState, setSortState] = useState<{
		column: keyof IParticipantsData | null;
		direction: 'asc' | 'desc';
	}>({
		column: null,
		direction: 'asc',
	});
	const [filteredAllValues, setFilteredAllValues] = useState<IParticipantsData[]>([]);
	const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
	const [isProfilePage, setIsProfilePage] = useState<boolean>(false);

	const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

	const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
		setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

		if (!isShowAttendeeDetailsModal && attendeeId) {
			try {
				await dispatch(getAttendee(attendeeId)).unwrap();
			} catch (error) {
				toast.error(getErrorMessage(error));
			}
		}

		if (isShowAttendeeDetailsModal) {
			getParticipantsData();
		}
	};

	const getParticipantsData = async () => {
		if (!currentEvent) return;

		try {
			const reqBody = {
				eventId: currentEvent?.id,
				page,
				searchName,
			};

			const {data} = await dispatch(getReportParticipants(reqBody)).unwrap();

			setFilteredAllValues(data);
			setFilterValues(data.slice(0, defaultPageSize));
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const sortData = (column: keyof IParticipantsData) => {
		const direction = sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';

		const sorted = [...filterValues].sort((a, b) => {
			const valA = a[column];
			const valB = b[column];

			if (typeof valA === 'number' && typeof valB === 'number') {
				return direction === 'asc' ? valA - valB : valB - valA;
			}

			return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
		});

		setSortState({column, direction});
		setFilterValues(sorted);
	};

	const handleSearchChange = (name: string) => {
		setSearchName(name);
		setPage(1);
	};

	const handlePaginationChange = (step: number) => {
		setPage(step);

		const startIdx = (step - 1) * defaultPageSize;
		const endIdx = startIdx + defaultPageSize;
		setFilterValues(filteredAllValues.slice(startIdx, endIdx) || []);
	};

	const handleDownloadCSV = async () => {
		if (!currentEvent) return;

		try {
			const file = await OrganizationReportsService.getParticipantsCsv(currentEvent?.id);
			downloadFile(file.data, REPORTS_GET_FILE.PARTICIPANTS);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const paginationText = participantList?.totalCount
		? `Showing ${(page - 1) * defaultPageSize + 1} - ${Math.min(
				page * defaultPageSize,
				participantList?.totalCount ?? 0
		  )} of ${participantList?.totalCount} guests`
		: 'Showing 0 guests';

	useEffect(() => {
		if (!participantList?.data) return;

		setFilteredAllValues(participantList?.data);
		setFilterValues(participantList?.data.slice(0, defaultPageSize));
	}, []);

	useEffect(() => {
		getParticipantsData();
	}, [currentEvent, page, searchName]);

	return (
		<Box sx={ReportsModalTablesStyle.wrapper}>
			<Box sx={ReportsModalTablesStyle.filterRow}>
				<Typography variant={SUBTITLE1} sx={ReportsModalTablesStyle.title}>
					{paginationText}
				</Typography>

				<Box sx={ReportsModalTablesStyle.search}>
					<SearchIcon />
					<Input placeholder="Search" value={searchName} onChange={handleSearchChange} />
				</Box>
				<Box>
					<Button
						sx={ReportsModalTablesStyle.download}
						startIcon={<ImportIcon />}
						onClick={handleDownloadCSV}
						title="Download.csv"
						disabled={!filterValues.length}
					/>
				</Box>
			</Box>

			{isLoading ? (
				<Loader parentClassName={ReportsModalTablesStyle.loader} size={50} />
			) : filterValues.length ? (
				<Box sx={ReportsModalTablesStyle.table}>
					<TableContainer>
						<Table sx={ReportsModalTablesStyle.tableStyle}>
							<TableHead>
								<TableRow sx={{height: '32px'}}>
									{columnOptions.map(({label, key}) => {
										const sortable =
											key === 'firstName' || key === 'lastName' || key === 'highestBid' || key === 'bidderId';
										return (
											<TableCell
												key={key}
												onClick={() => sortable && sortData(key)}
												sx={{cursor: sortable ? 'pointer' : 'default'}}
											>
												<Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
													{label}
													{sortable && <ArrowUpDownIcon />}
												</Box>
											</TableCell>
										);
									})}
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{filterValues.map((row, index) => (
									<TableRow key={index}>
										{columnOptions.map(({key}) => (
											<TableCell key={key}>
												<Typography
													className={key === 'highestBid' ? 'lastItem' : ''}
													sx={{
														color:
															typeof row[key] === 'number' && key === 'highestBid'
																? (row[key] as number) < 0
																	? 'var(--error-700) !important'
																	: 'var(--success-700) !important'
																: 'var(--neutral-900) !important',
													}}
												>
													{typeof row[key] === 'number' && key === 'highestBid' ? `$${row[key]}` : row[key] || '-'}
												</Typography>
											</TableCell>
										))}
										<TableCell>
											<ActionsMenu
												content={
													<Box sx={ReportsModalTablesStyle.menu}>
														<Typography
															sx={ReportsModalTablesStyle.menuContent}
															variant={BODY1}
															onClick={() => handleChangeAttendeeDetailsModal(row.bidderId)}
														>
															Go to Profile
														</Typography>
													</Box>
												}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<Box sx={ReportsModalTablesStyle.paginationContainer}>
						<Pagination
							page={page}
							pageSize={defaultPageSize}
							total={participantList?.totalCount || 0}
							onChange={handlePaginationChange}
						/>
					</Box>
				</Box>
			) : (
				<EmptyTableData />
			)}

			<Modal
				onClose={handleChangeAttendeeDetailsModal}
				open={isShowAttendeeDetailsModal && !!attendee}
				sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
			>
				<AttendeeDetails
					onCloseModal={handleChangeAttendeeDetailsModal}
					handleProfileTabStyles={handleProfileTabStyles}
				/>
			</Modal>
		</Box>
	);
};

export default AuctionParticipantsModal;
